import { Panel } from '../core/Panel';
import {
  Sim, MillSystem, PlotSystem, ConstructionSystem,
  MILL_INPUT_WHEAT, MILL_INPUT_CORN,
  MILL_OUTPUT_WHEAT, MILL_OUTPUT_CORN,
} from '../../game/simulation';
import { ICON } from '../icons';

type FlourKind = 'wheat' | 'corn';

/**
 * Mill panel — bound to a specific tile via setMill(c, r). PanelManager calls
 * setMill() before open() when handling an inspect:'mill' event.
 *
 * The panel exposes two flour recipes (wheat → 🍞, corn → 🌽-flour) each as
 * a card with a quantity stepper. Committing a card appends N batches to
 * the mill's queue. A queue list shows pending entries with the head one
 * tagged as "grinding".
 *
 * The per-kind quantity stepper (text input + ± buttons) lives on the
 * panel instance and is NOT part of the panel's hash — that way live typing
 * in the input doesn't trigger a re-render that would yank the focus.
 * Stepper buttons mutate the DOM directly + bump qty; the input's
 * `input` event listener keeps qty in sync as the player types.
 */
export class MillPanel extends Panel {
  private coord: { c: number; r: number } | null = null;
  /** Transient per-kind quantity selectors. Not hashed. */
  private qty: Record<FlourKind, number> = { wheat: 1, corn: 1 };
  /** True once the panel-root 'input' listener is wired (mount runs once). */
  private inputListenerWired = false;

  constructor(sim: Sim) { super(sim); }
  id() { return 'mill'; }
  title() { return 'Windmill'; }

  setMill(c: number, r: number) { this.coord = { c, r }; }

  hashInputs() {
    const coord = this.coord;
    const tile = coord ? PlotSystem.tile(this.sim.state, coord.c, coord.r) : null;
    const r = this.sim.state.res;
    // CRITICAL: round `remaining` to whole seconds. Including the raw float
    // here made the panel re-render every animation frame (60×/sec) while
    // milling, which rewrote innerHTML between mousedown and mouseup and
    // ate clicks on the X / Cancel / Demolish buttons.
    return JSON.stringify({
      coord,
      remaining: Math.ceil(tile?.millRemaining ?? 0),
      queue: tile?.millQueue ?? [],
      construction: tile?.construction ?? null,
      bld: tile?.bld ?? null,
      money: r.money, wood: r.wood, stone: r.stone ?? 0,
      basket: r.basket, corn: r.corn,
      flour: r.flour, cornFlour: r.cornFlour ?? 0,
    });
  }

  body() {
    const coord = this.coord;
    const tile = coord ? PlotSystem.tile(this.sim.state, coord.c, coord.r) : null;
    if (!tile || tile.bld !== 'mill') {
      return `<div class="ui-tip">Click a Windmill to manage it.</div>`;
    }
    // Construction takeover — partial-deposit flow
    if (tile.construction) {
      const cs = tile.construction;
      const cost = ConstructionSystem.nextStageCost(this.sim.state, coord!.c, coord!.r) ?? {};
      const paid = cs.stagePaid ?? { money: 0, wood: 0, wheat: 0, stone: 0 };
      const res = this.sim.state.res;
      const matRows: { label: string; need: number; paid: number; have: number }[] = [];
      if ((cost.money ?? 0) > 0) matRows.push({ label: `<span class="glyph glyph-inline">${ICON.coin}</span> Coins`, need: cost.money!, paid: paid.money, have: res.money });
      if ((cost.wood  ?? 0) > 0) matRows.push({ label: `<span class="glyph glyph-inline">${ICON.wood}</span> Wood`,  need: cost.wood!,  paid: paid.wood,  have: res.wood });
      if ((cost.stone ?? 0) > 0) matRows.push({ label: `<span class="glyph glyph-inline">${ICON.stone}</span> Stone`, need: cost.stone!, paid: paid.stone, have: res.stone ?? 0 });
      if ((cost.wheat ?? 0) > 0) matRows.push({ label: `<span class="glyph glyph-inline">${ICON.wheat}</span> Wheat`, need: cost.wheat!, paid: paid.wheat, have: res.basket });
      // Can deposit if any material is short AND the player has some of it.
      const canDeposit = matRows.some(m => m.paid < m.need && m.have > 0);
      const matsHTML = matRows.map(m => {
        const remaining = Math.max(0, m.need - m.paid);
        const pct = m.need > 0 ? Math.min(100, Math.round(m.paid / m.need * 100)) : 100;
        return `
          <div class="ui-row" title="${m.have} in storage">
            <span>${m.label}</span>
            <span>
              <strong>${m.paid} / ${m.need}</strong>
              <span class="ui-cargo__sub">${remaining > 0 ? `(need ${remaining}, have ${m.have})` : '✓'}</span>
            </span>
          </div>
          <div class="ui-bar"><div class="ui-bar__fill" style="width:${pct}%"></div></div>`;
      }).join('');
      return `
        <div class="ui-section">
          <div class="ui-section__title">Under construction @(${coord!.c}, ${coord!.r})</div>
          <div class="ui-row"><span>Stage</span><strong>${cs.stage} / ${cs.totalStages}</strong></div>
          <div class="ui-bar"><div class="ui-bar__fill" style="width:${(cs.stage / cs.totalStages) * 100}%"></div><div class="ui-bar__label">${Math.floor((cs.stage / cs.totalStages) * 100)}%</div></div>
        </div>
        <div class="ui-section">
          <div class="ui-section__title">Crate — next stage materials</div>
          ${matsHTML || '<div class="ui-tip">No materials required for the next stage.</div>'}
        </div>
        <button class="ui-btn ui-btn--primary ui-btn--block" data-act="advance" ${canDeposit ? '' : 'disabled'}
          title="Stages materials into the on-site crate. When the crate covers the full cost the stage advances automatically.">
          Deposit materials
        </button>
        <button class="ui-btn ui-btn--danger ui-btn--block" data-act="demolish">
          Cancel construction (50% refund)
        </button>
        <div class="ui-tip">Bring whatever you have — the crate stages partial deposits. The stage finishes the moment it's full.</div>
      `;
    }

    const res = this.sim.state.res;
    const queue = tile.millQueue ?? [];
    const busy = MillSystem.isBusy(tile);
    const progress = (tile.millRemaining ?? 0) > 0 ? Math.floor(MillSystem.progress(tile) * 100) : 0;
    const head = queue[0] ?? null;

    // Per-card data
    const cards: Array<{
      kind: FlourKind; icon: string; outIcon: string; label: string;
      input: number; output: number; have: number; produced: number;
    }> = [
      { kind: 'wheat', icon: ICON.wheat, outIcon: ICON.flour, label: 'Wheat flour',
        input: MILL_INPUT_WHEAT, output: MILL_OUTPUT_WHEAT, have: res.basket,
        produced: res.flour },
      { kind: 'corn',  icon: ICON.corn,  outIcon: ICON.flour, label: 'Corn flour',
        input: MILL_INPUT_CORN,  output: MILL_OUTPUT_CORN,  have: res.corn,
        produced: res.cornFlour ?? 0 },
    ];

    const cardHtml = cards.map(card => {
      const q = this.qty[card.kind];
      const canAfford = card.have >= card.input * q;
      return `
        <div class="ui-mill-card" data-kind="${card.kind}">
          <div class="ui-mill-card__head">
            <span class="ui-mill-card__icon">${card.icon}</span>
            <span class="ui-mill-card__title">${card.label}</span>
          </div>
          <div class="ui-row">
            <span>Recipe</span>
            <strong>${card.input}<span class="glyph glyph-inline">${card.icon}</span> → ${card.output}<span class="glyph glyph-inline">${card.outIcon}</span></strong>
          </div>
          <div class="ui-row">
            <span>In storage</span>
            <strong>${card.have}<span class="glyph glyph-inline">${card.icon}</span></strong>
          </div>
          <div class="ui-row">
            <span>Produced</span>
            <strong>${card.produced}<span class="glyph glyph-inline">${card.outIcon}</span></strong>
          </div>
          <div class="ui-mill-stepper">
            <button class="ui-btn ui-mill-stepper__btn" data-act="dec" data-kind="${card.kind}" data-step="1">−1</button>
            <input class="ui-mill-stepper__input" type="number" min="1" step="1" value="${q}" data-act="qty" data-kind="${card.kind}" />
            <button class="ui-btn ui-mill-stepper__btn" data-act="inc" data-kind="${card.kind}" data-step="1">+1</button>
            <button class="ui-btn ui-mill-stepper__btn" data-act="inc" data-kind="${card.kind}" data-step="10">+10</button>
          </div>
          <button class="ui-btn ui-btn--primary ui-btn--block" data-act="enqueue" data-kind="${card.kind}">
            Queue <span data-qty-label="${card.kind}">${q}</span> batch${q === 1 ? '' : 'es'}
          </button>
          ${!canAfford ? `<div class="ui-warn">⚠ Need ${card.input * q}${card.icon} for ${q} batch${q === 1 ? '' : 'es'} (have ${card.have}).</div>` : ''}
        </div>
      `;
    }).join('');

    // Queue list
    let queueHtml = '';
    if (queue.length === 0) {
      queueHtml = `<div class="ui-tip">Queue is empty — pick a flour and tap "Queue".</div>`;
    } else {
      const items = queue.map((entry, i) => {
        const isHead = i === 0 && (tile.millRemaining ?? 0) > 0;
        const icon = entry.kind === 'wheat' ? ICON.wheat : ICON.corn;
        const status = isHead ? `grinding (${progress}%)` : (i === 0 ? 'next up' : 'waiting');
        return `<li class="ui-mill-q-item${isHead ? ' ui-mill-q-item--active' : ''}">
          <span class="ui-mill-q-icon">${icon}</span>
          <span class="ui-mill-q-qty">×${entry.qty}</span>
          <span class="ui-mill-q-status">${status}</span>
        </li>`;
      }).join('');
      queueHtml = `<ul class="ui-mill-queue">${items}</ul>`;
    }

    const headProgressBar = (tile.millRemaining ?? 0) > 0
      ? `<div class="ui-bar"><div class="ui-bar__fill" style="width:${progress}%"></div><div class="ui-bar__label">${head ? `${head.kind === 'wheat' ? ICON.wheat : ICON.corn} ` : ''}${progress}%</div></div>`
      : '';

    return `
      <div class="ui-section">
        <div class="ui-section__title">At (${coord!.c}, ${coord!.r})</div>
        <div class="ui-row"><span>Status</span><strong>${busy ? 'Running' : 'Idle'}</strong></div>
      </div>
      ${headProgressBar}
      <div class="ui-mill-cards">${cardHtml}</div>
      <div class="ui-section">
        <div class="ui-section__title">Queue (${queue.reduce((s, e) => s + e.qty, 0)} batch${queue.reduce((s, e) => s + e.qty, 0) === 1 ? '' : 'es'})</div>
        ${queueHtml}
      </div>
      <button class="ui-btn ui-btn--danger ui-btn--block" data-act="cancel-queue" ${queue.length === 0 ? 'disabled' : ''}>
        Cancel queue
      </button>
      <button class="ui-btn ui-btn--danger ui-btn--block" data-act="demolish">
        Demolish this Mill (50% refund)
      </button>
    `;
  }

  /** Update the displayed qty in the DOM for a kind without forcing a
   *  full re-render (the input's `value` is set live; the button label is
   *  patched too so the count matches). Called after the player clicks
   *  the +/- stepper buttons. */
  private patchQty(kind: FlourKind) {
    const q = this.qty[kind];
    const input = this.root.querySelector(
      `input[data-act="qty"][data-kind="${kind}"]`,
    ) as HTMLInputElement | null;
    if (input && document.activeElement !== input) input.value = String(q);
    const label = this.root.querySelector(
      `[data-qty-label="${kind}"]`,
    ) as HTMLElement | null;
    if (label) label.textContent = String(q);
  }

  /** Click handler — quantity stepper, enqueue, cancel, demolish, advance. */
  onClick(e: Event) {
    const t = e.target as HTMLElement;
    const actEl = t.closest('[data-act]') as HTMLElement | null;
    if (!actEl || !this.coord) return;
    const act = actEl.dataset.act;
    const kindAttr = actEl.dataset.kind as FlourKind | undefined;

    if (act === 'inc' || act === 'dec') {
      if (!kindAttr) return;
      const step = Math.max(1, parseInt(actEl.dataset.step ?? '1', 10) || 1);
      const next = act === 'inc' ? this.qty[kindAttr] + step : this.qty[kindAttr] - step;
      this.qty[kindAttr] = Math.max(1, next);
      this.patchQty(kindAttr);
      return;
    }
    if (act === 'enqueue') {
      if (!kindAttr) return;
      const q = Math.max(1, Math.floor(this.qty[kindAttr] || 1));
      this.sim.enqueueMillBatch(this.coord.c, this.coord.r, kindAttr, q);
      // Reset the stepper to 1 after committing so repeat taps don't
      // accidentally over-charge.
      this.qty[kindAttr] = 1;
      this.patchQty(kindAttr);
      return;
    }
    if (act === 'cancel-queue') {
      if (confirm('Empty the mill queue? The current batch will finish anyway.')) {
        this.sim.cancelMillQueue(this.coord.c, this.coord.r);
      }
      return;
    }
    if (act === 'advance') {
      // Partial-deposit flow — moves whatever materials the player has
      // into the on-site crate. When the crate matches the stage cost
      // the stage advances automatically inside Sim.
      this.sim.depositForBuilding(this.coord.c, this.coord.r);
      return;
    }
    if (act === 'demolish') {
      if (confirm('Tear down this Windmill? You\'ll get half the cost back.')) {
        this.sim.demolish(this.coord.c, this.coord.r);
        this.hide();
      }
      return;
    }
  }

  /** Wire a delegated 'input' listener once so number-input keystrokes
   *  keep `this.qty` in sync without triggering a re-render (the input
   *  isn't part of hashInputs). */
  mount(parent: HTMLElement) {
    super.mount(parent);
    if (this.inputListenerWired) return;
    this.inputListenerWired = true;
    this.root.addEventListener('input', (e: Event) => {
      const el = e.target as HTMLInputElement;
      if (!el || el.dataset?.act !== 'qty') return;
      const kind = el.dataset.kind as FlourKind | undefined;
      if (!kind) return;
      const v = Math.max(1, Math.floor(parseInt(el.value, 10) || 1));
      this.qty[kind] = v;
    });
  }
}
